<script lang="ts">
  // Full-screen paywall reached when a server 402 fires — which now happens only
  // when EVERY community the creator holds is expired (a single expired community
  // is gated in-shell instead, see AdminShell). The Upgrade CTA runs a per-skool
  // Stripe Checkout (via the shared ExpiredPaywall) for the active/expired
  // community; "Check again" re-reads billing state and drops back into the app
  // as soon as any community is live again.
  //
  // WAVE 6: "Check again" was `GET /admin/me` → `GET /admin/communities`, two
  // sequential requests to learn one account's plan state and one list. It is one
  // now — `refreshCommunities()` forces the bootstrap (`@api/v1/me`) and the
  // account arrives in the same body as the communities, which is the point of
  // reading them together: a stale account `plan_status` beside a fresh community
  // one is exactly what sends a creator who has just paid back to the paywall.
  import { navigate } from '../lib/router.svelte';
  import { getToken, handle401, clearToken, type AdminUser, type Community } from '../lib/admin';
  import { community, refreshCommunities } from '../lib/community.svelte';
  import { asAdminUser, cachedBootstrap } from '../lib/session';
  import ExpiredPaywall from '../lib/ui/ExpiredPaywall.svelte';

  let me = $state<AdminUser | null>(null);
  let checking = $state(false);
  let justChecked = $state(false);

  // The community to upgrade: prefer the active selection (the one they were last
  // looking at); otherwise any expired community they hold.
  const target = $derived<Community | null>(
    community.list.find((c) => c.id === community.selectedId && c.plan_status === 'expired') ??
      community.list.find((c) => c.plan_status === 'expired') ??
      null,
  );

  async function check(): Promise<void> {
    if (!getToken()) {
      navigate('/admin/login');
      return;
    }
    checking = true;
    try {
      await refreshCommunities();
      const boot = cachedBootstrap();
      if (boot) me = asAdminUser(boot);
      // As soon as ANY community is live again (upgraded / comped), the 402 no
      // longer fires — drop back into the app; the shell gates the rest per-skool.
      const allExpired =
        community.list.length > 0 && community.list.every((c) => c.plan_status === 'expired');
      if (!allExpired) {
        navigate('/admin');
        return;
      }
      justChecked = true;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
    } finally {
      checking = false;
    }
  }

  function signOut(): void {
    clearToken();
    navigate('/admin/login');
  }

  void check();
</script>

{#if !me && checking}
  <div class="console-glow flex min-h-screen items-center justify-center">
    <span class="loading loading-spinner loading-lg text-primary/70"></span>
  </div>
{:else}
  <div class="console-glow flex min-h-screen flex-col items-center justify-center gap-6 px-6 py-12">
    <ExpiredPaywall skoolId={target?.id ?? null} communityName={target?.name ?? null} />

    <div class="flex flex-col items-center gap-3 text-center">
      <button class="btn btn-ghost btn-sm gap-2" onclick={check} disabled={checking}>
        {#if checking}<span class="loading loading-spinner loading-sm"></span>{/if}
        Check again
      </button>
      {#if justChecked && !checking}
        <span class="text-sm text-base-content/68">
          Still expired — once your payment clears this updates automatically.
        </span>
      {/if}
      <p class="text-sm text-base-content/68">
        Signed in as <span class="redact">{me?.email ?? ''}</span> ·
        <button class="link link-hover" onclick={signOut}>sign out</button>
      </p>
    </div>
  </div>
{/if}
